import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";

import * as client from "./client";
import { trackLead, trackInitiateSignup } from "helpers/analytics";

const WaitlistContext = createContext();

const initReducer = () => ({
  status: "idle", // idle | loading | success | error
  modalOpen: false,
});

const reducer = (state, { action, value }) => {
  switch (action) {
    case "setStatus":
      return { ...state, status: value };
    case "setModalOpen":
      return { ...state, modalOpen: value };
    default:
      throw new Error(`Unexpected action: ${action}`);
  }
};

export const WaitlistProvider = (props) => {
  const [state, dispatch] = useReducer(reducer, props, initReducer);
  const value = useMemo(() => [state, dispatch], [state]);

  return <WaitlistContext.Provider value={value} {...props} />;
};

export const useWaitlist = () => {
  const context = useContext(WaitlistContext);
  if (!context) {
    throw new Error("useWaitlist must be used inside a WaitlistProvider");
  }
  const [state, dispatch] = context;

  const submit = useCallback(
    async (email) => {
      if (!email.trim()) return;
      dispatch({ action: "setStatus", value: "loading" });
      try {
        await client.submit({ email: email.trim() });
        dispatch({ action: "setStatus", value: "success" });
        trackLead({ email: email.trim() });
      } catch {
        dispatch({ action: "setStatus", value: "error" });
      }
    },
    [dispatch],
  );

  const openModal = useCallback(() => {
    dispatch({ action: "setModalOpen", value: true });
    trackInitiateSignup();
  }, [dispatch]);

  const closeModal = useCallback(() => {
    dispatch({ action: "setModalOpen", value: false });
  }, [dispatch]);

  return {
    state,
    dispatch,
    submit,
    openModal,
    closeModal,
  };
};

const waitlist = { WaitlistProvider, useWaitlist };
export default waitlist;
